import React from "react";
import { Route, Redirect } from "react-router-dom";
import { getCurrentUser } from "../../services/auth_service";
import { getActiveRole, hasPermission, hasAnyPermission } from "../../utils/permissions";
import { useAuth } from "../../services/authProvider";

/**
 * PermissionProtectedRoute - A route component that checks for required permissions
 *
 * @param {string} path - The route path
 * @param {Component} component - The component to render if authorized
 * @param {Array<string>} allowedRoles - Array of role names that can access this route
 * @param {string} requiredPermission - Single permission required to access the route
 * @param {Array<string>} requiredPermissions - Array of permissions (user needs at least one)
 * @param {boolean} requireAllPermissions - If true, user must have all permissions in requiredPermissions
 * @param {Function} render - Alternative render function
 * @param {Object} rest - Other route props
 */
const PermissionProtectedRoute = ({
  path,
  component: Component,
  allowedRoles,
  requiredPermission,
  requiredPermissions,
  requireAllPermissions = false,
  render,
  ...rest
}) => {
  const { accessToken } = useAuth();

  const getUserRole = () => {
    return getActiveRole();
  };

  const checkPermissions = () => {
    // If a single permission is specified
    if (requiredPermission) {
      return hasPermission(requiredPermission);
    }

    // If multiple permissions are specified
    if (requiredPermissions && requiredPermissions.length > 0) {
      if (requireAllPermissions) {
        // Check if user has all permissions
        return requiredPermissions.every(permission => hasPermission(permission));
      } else {
        // Check if user has at least one permission
        return hasAnyPermission(requiredPermissions);
      }
    }

    // No permissions specified, allow access (role-based only)
    return true;
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        // Check if user is authenticated
        if (!accessToken || !getCurrentUser()) {
          return (
            <Redirect
              to={{ pathname: "/user/login", state: { from: props.location } }}
            />
          );
        }

        // Check if user has the required role
        const userRole = getUserRole();

        if (!userRole) {
          return (
            <Redirect
              to={{ pathname: "/user/role", state: { from: props.location } }}
            />
          );
        }

        // Check if the user's role is in the allowed roles list
        if (allowedRoles && !allowedRoles.includes(userRole)) {
          return (
            <Redirect
              to={{ pathname: "/hcp/patient/unauthorized", state: { from: props.location } }}
            />
          );
        }

        // Check permissions (only for HEALTH_CARE_PROVIDER role)
        if (userRole === 'HEALTH_CARE_PROVIDER') {
          const hasRequiredPermissions = checkPermissions();

          if (!hasRequiredPermissions) {
            return (
              <Redirect
                to={{
                  pathname: "/hcp/patient/unauthorized",
                  state: {
                    from: props.location,
                    message: "You do not have permission to access this page."
                  }
                }}
              />
            );
          }
        }

        // User is authenticated, has the correct role, and has required permissions
        return Component ? <Component {...props} /> : render(props);
      }}
    />
  );
};

export default PermissionProtectedRoute;
